import React from 'react'
import moment from 'moment'
import DashboardEditableRow from '@components/pages/Dashboard/DashboardEditableRow.component'
import {
  AROver90DayCard,
  NetHygieneProductionCard,
  ProductionPerHygieneVisitCard,
} from '@components/pages/Dashboard/Finance/SectionCards'
import type { RangeKey } from '@dataTypes/dashboard.types'
import { DashboardSection } from '@constants/Dashboard/dashboard.enums'
import { CALENDAR_DATE_FORMAT } from '@utils/dashboardFinance.util'
import {
  InsuranceClaimsCard,
  PatientsAgeCard,
  PatientStatusCard,
  RevenueOverviewCard,
  ScheduleOperationsCard,
  SeatUtilizationCard,
} from './SectionCards'
import { useOperationsDashboard } from './useOperationsDashboard'
import '@components/pages/Dashboard/DashboardLayout.style.css'
import '@components/pages/Dashboard/Shared/DashboardMetricTileGrid.style.css'
import './DashboardOperations.style.css'

type DashboardOperationsProps = {
  editMode?: boolean
  hiddenSections?: Set<string>
  range?: RangeKey
  date?: string
}

function DashboardOperations({
  editMode = false,
  hiddenSections = new Set(),
  range = '1D',
  date = moment().format(CALENDAR_DATE_FORMAT),
}: DashboardOperationsProps): React.ReactElement {
  const show = (section: DashboardSection): boolean =>
    !hiddenSections.has(section)
  const operations = useOperationsDashboard({ range, date, hiddenSections })

  return (
    <div className="dashboard-operations">
      {show(DashboardSection.RevenueOverview) && (
        <RevenueOverviewCard
          titleHref={operations.hrefs.revenueOverview}
          revenue={operations.revenueApi}
        />
      )}

      {(show(DashboardSection.ScheduleOperations) ||
        show(DashboardSection.InsuranceClaims)) && (
        <DashboardEditableRow
          editMode={editMode}
          className="dashboard-operations-two-cards-row"
        >
          {show(DashboardSection.ScheduleOperations) && (
            <ScheduleOperationsCard
              titleHref={operations.hrefs.scheduleOperations}
              range={range}
              loading={operations.scheduleOperationsApi.loading}
              scheduleOpsOverview={operations.scheduleOperationsApi.data}
            />
          )}
          {show(DashboardSection.InsuranceClaims) && (
            <InsuranceClaimsCard
              titleHref={operations.hrefs.insuranceClaims}
              claims={operations.insuranceClaimsApi}
            />
          )}
        </DashboardEditableRow>
      )}

      {(show(DashboardSection.NetHygieneProduction) ||
        show(DashboardSection.ProductionPerHygieneVisit) ||
        show(DashboardSection.ArOver90Day)) && (
        <DashboardEditableRow
          editMode={editMode}
          className="dashboard-operations-four-cards-row"
        >
          {show(DashboardSection.NetHygieneProduction) && (
            <NetHygieneProductionCard
              titleHref={operations.hrefs.netHygieneProduction}
              metric={operations.hygieneApi.netHygieneMetric}
              loading={operations.hygieneApi.loading}
              hasPayload={operations.hygieneApi.hasPayload}
              hasData={operations.hygieneApi.hasNetHygieneData}
              className="dashboard-operations-hygiene-card"
            />
          )}
          {show(DashboardSection.ProductionPerHygieneVisit) && (
            <ProductionPerHygieneVisitCard
              titleHref={operations.hrefs.productionPerHygieneVisit}
              metric={operations.hygieneApi.productionPerVisitMetric}
              loading={operations.hygieneApi.loading}
              hasPayload={operations.hygieneApi.hasPayload}
              hasData={operations.hygieneApi.hasProductionPerVisitData}
              className="dashboard-operations-hygiene-card"
            />
          )}
          {show(DashboardSection.ArOver90Day) && (
            <AROver90DayCard
              titleHref={operations.hrefs.arOver90Day}
              arOver90={operations.arOver90Api}
              className="dashboard-operations-hygiene-card"
            />
          )}
        </DashboardEditableRow>
      )}

      {(show(DashboardSection.PatientsAge) ||
        show(DashboardSection.PatientStatus)) && (
        <DashboardEditableRow
          editMode={editMode}
          className="dashboard-operations-two-cards-row"
        >
          {show(DashboardSection.PatientsAge) && (
            <PatientsAgeCard
              titleHref={operations.hrefs.patientsAge}
              chartData={operations.patientsAgeApi.chartData}
              axis={operations.patientsAgeApi.axis}
              loading={operations.patientsAgeApi.loading}
              hasPayload={operations.patientsAgeApi.hasPayload}
            />
          )}
          {show(DashboardSection.PatientStatus) && (
            <PatientStatusCard
              titleHref={operations.hrefs.patientStatus}
              chartData={operations.patientStatusApi.chartData}
              axis={operations.patientStatusApi.axis}
              loading={operations.patientStatusApi.loading}
              hasPayload={operations.patientStatusApi.hasPayload}
            />
          )}
        </DashboardEditableRow>
      )}

      {show(DashboardSection.SeatUtilization) && (
        <DashboardEditableRow
          editMode={editMode}
          className="dashboard-operations-two-cards-row"
        >
          <SeatUtilizationCard
            titleHref={operations.hrefs.seatUtilization}
            metrics={operations.seatUtilizationApi.cardMetrics}
            chartData={operations.seatUtilizationApi.donutSlices}
            loading={operations.seatUtilizationApi.loading}
            hasPayload={operations.seatUtilizationApi.hasPayload}
          />
        </DashboardEditableRow>
      )}
    </div>
  )
}

export default DashboardOperations
